import { Chart as ChartJS, ChartOptions, TooltipModel } from 'chart.js';

import { AssetItem, DistributionRecord, IWBeneficiary } from '@/entities/interwills';
import { COLORS } from '@/shared/constants/colors';
import { DtoId } from '@/shared/lib/sj-orm/models/base.dto';
import { numbers } from '@/shared/utils/numbers';

import { ChartType as DistributionChartType } from './AssetDitribution';

import { styles } from './charts/styles';

type ChartType = 'bar' | 'doughnut';
type ChartValueType = 'value' | 'price' | 'percent' | 'valueAndPercent';

export const getOrCreateTooltip = (chart: ChartJS): HTMLElement => {
  let tooltipEl = chart.canvas.parentNode?.querySelector<HTMLElement>('div[data-tooltip="custom"]');

  if (!tooltipEl) {
    tooltipEl = document.createElement('div');
    tooltipEl.setAttribute('data-tooltip', 'custom');
    tooltipEl.innerHTML = `
      <div class="tooltip-title"></div>
      <div class="tooltip-body"></div>
      <div class="tooltip-caret"></div>
    `;

    Object.assign(tooltipEl.style, styles.tooltip);

    const caret = tooltipEl.querySelector<HTMLElement>('.tooltip-caret');

    if (caret) {
      Object.assign(caret.style, styles.caret);
      chart.canvas.parentNode?.appendChild(tooltipEl);
    }
  }

  return tooltipEl;
};

export const externalTooltipHandler = (context: {
  chart: ChartJS;
  chartType: ChartType;
  tooltip: TooltipModel<ChartType>;
  valueType: ChartValueType;
  stackBars: boolean;
  customTooltipValueDisplay?: (dataValue: number, dataIndex: number) => string | number;
}) => {
  const { chart, tooltip, valueType, chartType, stackBars, customTooltipValueDisplay } = context;
  const tooltipEl = getOrCreateTooltip(chart);

  if (tooltip.opacity === 0) {
    tooltipEl.style.opacity = '0';
    return;
  }

  if (tooltip.body) {
    const dataPoint = tooltip.dataPoints[0];
    const dataset = dataPoint.dataset;
    const datasetLabel = dataset.label || '';
    const dataIndex = dataPoint.dataIndex;
    const datasetIndex = dataPoint.datasetIndex;
    const dataValue = dataset.data[dataIndex] as number;
    const color = (dataPoint.element.options.backgroundColor || dataset.backgroundColor) as string;
    const titleEl = tooltipEl.querySelector<HTMLElement>('.tooltip-title');

    if (titleEl) {
      titleEl.innerText = datasetLabel;
      Object.assign(titleEl.style, styles.title);
    }

    const bodyEl = tooltipEl.querySelector('.tooltip-body') as HTMLElement;
    let displayValue: string | number;
    if (customTooltipValueDisplay) {
      displayValue = customTooltipValueDisplay(dataValue, dataIndex);
    } else if (valueType === 'valueAndPercent') {
      const total = (dataset.data as number[]).reduce((sum, val) => sum + val, 0);
      const percentage = total > 0 ? (dataValue / total) * 100 : 0;
      displayValue = `<b>${dataValue}</b> (${numbers.formatPercent({
        value: percentage,
        withSign: true,
        fractionDigits: 1,
      })})`;
    } else {
      displayValue =
        valueType === 'percent'
          ? numbers.formatPercent({ value: dataValue, withSign: true, fractionDigits: 0 })
          : valueType === 'price'
          ? numbers.formatPrice(dataValue)
          : dataValue;
    }

    const label =
      chart.data.labels?.[chartType === 'bar' && stackBars ? datasetIndex : dataIndex] || '';

    bodyEl.innerHTML = `
      <span style="background:${color}; width:12px; height:12px; border-radius: 2px; display:inline-block; margin-right: 8px; vertical-align: middle;"></span>
      <span style="vertical-align: middle;">${label}: ${displayValue}</span>
    `;

    Object.assign(bodyEl.style, {
      fontSize: '12px',
    });
  }

  const { offsetLeft: positionX, offsetTop: positionY } = chart.canvas;
  const tooltipWidth = tooltipEl.offsetWidth;
  const windowWidth = window.innerWidth;

  let left = positionX + tooltip.caretX;

  if (left - tooltipWidth / 2 < 0) {
    left = tooltipWidth / 2;
  }

  if (left + tooltipWidth / 2 > windowWidth) {
    left = windowWidth - tooltipWidth / 2;
  }

  tooltipEl.style.opacity = '1';
  tooltipEl.style.left = `${left}px`;
  tooltipEl.style.top = `${positionY + tooltip.caretY - tooltipEl.clientHeight - 10}px`;
  tooltipEl.style.transform = 'translate(-50%, 0)';
};

export const getChartOptions = (
  valueType: ChartValueType = 'value',
  chartType: ChartType,
  options: {
    stackBars: boolean;
    customTooltipValueDisplay?: (dataValue: number, dataIndex: number) => string | number;
  } = { stackBars: true },
): ChartOptions<ChartType> => ({
  indexAxis: 'x',
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      display: false,
    },
    tooltip: {
      enabled: false,
      external: (props) =>
        externalTooltipHandler({
          ...props,
          valueType,
          chartType,
          stackBars: options.stackBars,
          customTooltipValueDisplay: options.customTooltipValueDisplay,
        }),
    },
    datalabels: {
      display: false,
    },
  },
  scales: {
    x: {
      stacked: true,
      grid: {
        display: false,
      },
      display: false,
    },
    y: {
      stacked: true,
      grid: {
        display: false,
      },
      display: false,
    },
  },
});

export const getDistributionColor = (index: number) => {
  return COLORS.colorAssetDistributionCharts[index % COLORS.colorAssetDistributionCharts.length];
};

export const getDistributionChartData = ({
  beneficiaries,
  distribution,
  assets,
  chartType,
}: {
  beneficiaries: IWBeneficiary[];
  distribution: DistributionRecord;
  assets: AssetItem[];
  chartType: DistributionChartType;
}) => {
  const getBeneficiaryNameFromId = (id: DtoId) => {
    const beneficiary = beneficiaries.find((item) => item.id === id);
    return beneficiary ? `${beneficiary.firstName} ${beneficiary.lastName}` : '';
  };

  const aggregates = Object.entries(distribution).reduce(
    (accumulator, [assetId, assetDistributions]) => {
      const currentAsset = assets.find((asset) => asset.id === assetId);

      assetDistributions.reduce((result, { primaryBeneficiary, percentage }) => {
        if (!primaryBeneficiary || !percentage || percentage <= 0) return result;

        const beneficiaryValue = (currentAsset?.purchaseValue?.value ?? 0) * (percentage / 100);
        const displayValue = chartType === 'shares' ? percentage : beneficiaryValue;
        const updatedBeneficiaryTotal = (result[primaryBeneficiary] || 0) + displayValue;
        result[primaryBeneficiary] = updatedBeneficiaryTotal;

        return result;
      }, accumulator);

      return accumulator;
    },
    {} as Record<string, number>,
  );

  const total = Object.values(aggregates).reduce((sum, val) => sum + val, 0);

  const legendItems = Object.entries(aggregates)
    .map(([id, rawValue], index) => ({
      id,
      name: getBeneficiaryNameFromId(id),
      rawValue,
      value: (rawValue / total) * 100,
      color: getDistributionColor(index),
    }))
    .sort((a, b) => b.value - a.value);

  return {
    labels: legendItems.map((item) => item.name),
    data: legendItems.map((item) => (chartType === 'shares' ? item.value : item.rawValue)),
    colors: legendItems.map((item) => item.color),
    legendItems,
  };
};
